import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Target, Pencil, Plus, Trash2, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const aud = (v) => `A$${Number(v || 0).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;

const GoalEditor = ({ data, authHeaders, onSaved, onCancel }) => {
  const [def, setDef] = useState(data.default);
  const [rows, setRows] = useState(Object.entries(data.overrides || {}).map(([month, goal]) => ({ month, goal })));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const overrides = Object.fromEntries(rows.filter((r) => r.month && r.goal).map((r) => [r.month, Number(r.goal)]));
      await axios.put(`${API}/admin/revenue-goal`, { default: Number(def), overrides }, authHeaders);
      toast.success('Revenue goals saved'); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Goals must be positive amounts'); }
    setSaving(false);
  };
  return (
    <div className="mt-5 pt-5 border-t border-[#E6DAC5] space-y-4" data-testid="revenue-goal-editor">
      <div>
        <label className="block text-sm font-medium text-[#1E1622] mb-2">Standing monthly goal (A$)</label>
        <input type="number" min="1" value={def} onChange={(e) => setDef(e.target.value)} data-testid="revenue-goal-default-input" className="w-48 p-3 border border-[#E6DAC5] rounded-xl text-sm" />
      </div>
      <div>
        <p className="text-sm font-medium text-[#1E1622] mb-2">Different goal for a specific month</p>
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-3 mb-2">
            <input type="month" value={r.month} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, month: e.target.value } : x)))} data-testid={`revenue-goal-override-month-${i}`} className="p-2 border border-[#E6DAC5] rounded-lg text-sm" />
            <input type="number" min="1" value={r.goal} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, goal: e.target.value } : x)))} data-testid={`revenue-goal-override-amount-${i}`} placeholder="A$" className="w-32 p-2 border border-[#E6DAC5] rounded-lg text-sm" />
            <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" aria-label="Remove"><Trash2 size={14} /></button>
          </div>
        ))}
        <button onClick={() => setRows([...rows, { month: '', goal: '' }])} data-testid="revenue-goal-add-override" className="text-sm text-[#3B164C] inline-flex items-center gap-1"><Plus size={14} /> Add a month</button>
      </div>
      <div className="flex gap-3">
        <button onClick={save} disabled={saving} data-testid="revenue-goal-save" className="btn-primary text-sm inline-flex items-center gap-2">{saving && <Loader2 size={14} className="animate-spin" />} Save goals</button>
        <button onClick={onCancel} className="text-sm text-[#1E1622]/60">Cancel</button>
      </div>
    </div>
  );
};

export const RevenueGoalCard = ({ authHeaders }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const load = () => axios.get(`${API}/admin/revenue-goal`, authHeaders).then((r) => setData(r.data)).catch(() => {});
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const c = data.current;
  const label = new Date(`${c.month}-01T00:00:00`).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  const expectedPct = Math.min(100, (100 * c.expected_by_now) / c.goal);
  return (
    <div className="bg-[#2D1037] text-[#FAF7F2] rounded-2xl p-6" data-testid="revenue-goal-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-1 inline-flex items-center gap-2"><Target size={14} /> {label} revenue goal</p>
          <p className="font-display text-3xl" data-testid="revenue-goal-progress">{aud(c.revenue)} <span className="text-[#FAF7F2]/50 text-xl">of {aud(c.goal)}</span></p>
        </div>
        <button onClick={() => setEditing((v) => !v)} data-testid="revenue-goal-edit-btn" className="text-sm text-[#E5C178] hover:text-[#C5A059] inline-flex items-center gap-1"><Pencil size={14} /> Set goals</button>
      </div>
      <div className="relative h-3 rounded-full bg-[#FAF7F2]/15 mb-3" data-testid="revenue-goal-bar">
        <div className="absolute inset-y-0 left-0 rounded-full bg-[#C5A059] transition-[width] duration-700" style={{ width: `${Math.min(100, c.pct)}%` }} />
        <div className="absolute -top-1 -bottom-1 w-0.5 bg-[#FAF7F2]/70" style={{ left: `${expectedPct}%` }} title="Where you'd be on an even pace" />
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-[#FAF7F2]/75">
        <span data-testid="revenue-goal-pct">{c.pct}% reached</span>
        <span data-testid="revenue-goal-days-left">{c.days_left} day{c.days_left === 1 ? '' : 's'} left</span>
        <span data-testid="revenue-goal-pace" className={c.on_pace ? 'text-[#9be3b0]' : 'text-[#f5c08a]'}>
          {c.on_pace ? 'On pace' : `Behind pace by ${aud(c.expected_by_now - c.revenue)}`}
        </span>
        {c.projected != null && <span>Projected: {aud(c.projected)}</span>}
      </div>
      {editing && <GoalEditor data={data} authHeaders={authHeaders} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
    </div>
  );
};
